import React, { useState, useEffect } from 'react'
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Button,
  AppBar,
  Toolbar,
  IconButton,
  Avatar,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton as MuiIconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  Chip,
  Divider,
  Alert
} from '@mui/material'
import {
  ArrowBack as ArrowBackIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Class as ClassIcon,
  Group as GroupIcon,
  Code as CodeIcon,
  School as SchoolIcon
} from '@mui/icons-material'
import { useAuth } from '../hooks/useAuth'
import { auth, db } from '../firebase/config'
import { collection, query, where, getDocs, doc, deleteDoc, updateDoc, arrayRemove } from 'firebase/firestore'
import toast from 'react-hot-toast'
import CreateClass from './CreateClass'
import ClassDetail from './ClassDetail'
import SpaceTheme from '../components/SpaceTheme'

const TeacherClasses = ({ userData, onBack }) => {
  const { user } = useAuth()
  const [classes, setClasses] = useState([])
  const [loading, setLoading] = useState(true)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [classToDelete, setClassToDelete] = useState(null)
  const [currentPage, setCurrentPage] = useState('classes')
  const [selectedClass, setSelectedClass] = useState(null)

  useEffect(() => {
    if (user && user.uid) {
      loadClasses()
    }
  }, [user])

  const loadClasses = async () => {
    if (!user || !user.uid) {
      console.log('User not ready yet')
      return
    }

    try {
      setLoading(true)
      const teacherId = user.uid
      
      console.log('🔍 Arama yapılan teacherId:', teacherId)
      console.log('👤 Mevcut kullanıcı:', user)
      
      const classesRef = collection(db, 'classes')
      let classesData = []
      
      // 1. Önce teacherId ile ara
      console.log('🔍 1. teacherId ile arama yapılıyor:', teacherId)
      const teacherIdQuery = query(classesRef, where('teacherId', '==', teacherId))
      const teacherIdSnapshot = await getDocs(teacherIdQuery)
      console.log('📚 teacherId ile bulunan sınıf sayısı:', teacherIdSnapshot.size)
      
      for (const doc of teacherIdSnapshot.docs) {
        const classData = { id: doc.id, ...doc.data() }
        console.log('📖 teacherId ile bulunan sınıf:', classData)
        classesData.push(classData)
      }
      
      // 2. Eğer teacherId ile bulunamadıysa, teacherEmail ile ara
      if (classesData.length === 0 && user.email) {
        console.log('🔍 2. teacherEmail ile arama yapılıyor:', user.email)
        const emailQuery = query(classesRef, where('teacherEmail', '==', user.email))
        const emailSnapshot = await getDocs(emailQuery)
        console.log('📧 teacherEmail ile bulunan sınıf sayısı:', emailSnapshot.size)
        
        for (const doc of emailSnapshot.docs) {
          const classData = { id: doc.id, ...doc.data() }
          console.log('📖 teacherEmail ile bulunan sınıf:', classData)
          classesData.push(classData)
        }
      }
      
      // 3. Eğer hala bulunamadıysa, teacherName ile ara
      if (classesData.length === 0 && userData?.username) {
        console.log('🔍 3. teacherName ile arama yapılıyor:', userData.username)
        const nameQuery = query(classesRef, where('teacherName', '==', userData.username))
        const nameSnapshot = await getDocs(nameQuery)
        console.log('👤 teacherName ile bulunan sınıf sayısı:', nameSnapshot.size)
        
        for (const doc of nameSnapshot.docs) {
          const classData = { id: doc.id, ...doc.data() }
          console.log('📖 teacherName ile bulunan sınıf:', classData)
          classesData.push(classData)
        }
      }
      
      // 4. Debug: Tüm sınıfları kontrol et
      console.log('🔍 4. Tüm sınıfları kontrol ediliyor...')
      const allClassesSnapshot = await getDocs(collection(db, 'classes'))
      console.log('🌍 Firestore\'daki toplam sınıf sayısı:', allClassesSnapshot.size)
      allClassesSnapshot.docs.forEach(doc => {
        const data = doc.data()
        console.log('📋 Sınıf:', { 
          id: doc.id, 
          teacherId: data.teacherId,
          teacherEmail: data.teacherEmail,
          teacherName: data.teacherName,
          className: data.className
        })
      })
      
      // 5. Her sınıf için öğrenci sayısını hesapla
      for (const classData of classesData) {
        const studentsRef = collection(db, 'users')
        const studentsQuery = query(
          studentsRef, 
          where('role', '==', 'student'),
          where('classIds', 'array-contains', classData.id)
        )
        const studentsSnapshot = await getDocs(studentsQuery)
        classData.studentCount = studentsSnapshot.size
        console.log(`👥 ${classData.className} sınıfında ${classData.studentCount} öğrenci`)
      }
      
      console.log('✅ Yüklenen sınıflar:', classesData)
      setClasses(classesData)
    } catch (error) {
      console.error('❌ Error loading classes:', error)
      toast.error('Sınıflar yüklenirken hata oluştu')
    } finally {
      setLoading(false)
    }
  }

  const handleDeleteClass = async () => {
    if (!classToDelete) return

    try {
      // Sınıfı sil
      await deleteDoc(doc(db, 'classes', classToDelete.id))
      
      // Sınıftaki tüm öğrencilerin classIds'inden bu sınıfı çıkar
      const studentsRef = collection(db, 'users')
      const studentsQuery = query(
        studentsRef, 
        where('role', '==', 'student'),
        where('classIds', 'array-contains', classToDelete.id)
      )
      const studentsSnapshot = await getDocs(studentsQuery)
      
      const updatePromises = studentsSnapshot.docs.map(studentDoc => 
        updateDoc(doc(db, 'users', studentDoc.id), {
          classIds: arrayRemove(classToDelete.id)
        })
      )
      
      await Promise.all(updatePromises)
      
      toast.success('Sınıf başarıyla silindi')
      setClasses(classes.filter(c => c.id !== classToDelete.id))
    } catch (error) {
      console.error('Error deleting class:', error)
      toast.error('Sınıf silinirken hata oluştu')
    } finally {
      setDeleteDialogOpen(false)
      setClassToDelete(null)
    }
  }

  const openDeleteDialog = (classItem) => {
    setClassToDelete(classItem)
    setDeleteDialogOpen(true)
  }

  const closeDeleteDialog = () => {
    setDeleteDialogOpen(false)
    setClassToDelete(null)
  }

  const handleCreateClass = () => {
    setCurrentPage('create-class')
  }

  const handleClassClick = (classItem) => {
    setCurrentPage('class-detail')
    setSelectedClass(classItem)
  }

  const handleBackToClasses = () => {
    setCurrentPage('classes')
    loadClasses() // Sınıfları yeniden yükle
  }

  // CreateClass sayfasını göster
  if (currentPage === 'create-class') {
    return <CreateClass userData={userData} onBack={handleBackToClasses} />
  }

  // ClassDetail sayfasını göster
  if (currentPage === 'class-detail' && selectedClass) {
    return <ClassDetail classData={selectedClass} onBack={handleBackToClasses} userData={userData} />
  }

  return (
    <SpaceTheme>
      {/* App Bar */}
      <AppBar position="static" sx={{ 
        background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
        boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
      }}>
        <Toolbar>
          <IconButton
            edge="start"
            color="inherit"
            onClick={onBack}
            sx={{ mr: 2 }}
          >
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            Sınıflarım
          </Typography>
        </Toolbar>
      </AppBar>

      {/* Main Content */}
      <Container maxWidth="md" sx={{ py: 3 }}>
        {/* Create Class Button */}
        <Button
          variant="contained"
          fullWidth
          startIcon={<AddIcon />}
          onClick={handleCreateClass}
          sx={{ 
            mb: 3, 
            py: 1.5,
            fontSize: '1.1rem',
            fontWeight: 'bold'
          }}
        >
          + Yeni Sınıf Oluştur
        </Button>

        {/* Classes List */}
        {!user || !user.uid ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography>Kullanıcı bilgileri yükleniyor...</Typography>
          </Box>
        ) : loading ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography>Sınıflar yükleniyor...</Typography>
          </Box>
        ) : classes.length === 0 ? (
          /* Empty State */
          <Card sx={{ textAlign: 'center', py: 4 }}>
            <CardContent>
              <SchoolIcon sx={{ fontSize: 80, color: '#6E6D6D', mb: 2 }} />
              <Typography variant="h6" color="#6E6D6D" gutterBottom>
                Sınıflarınız bulunamadı
              </Typography>
              <Typography variant="body2" color="#6E6D6D" sx={{ mb: 2 }}>
                Sınıflarınızı görmek için lütfen konsol loglarını kontrol edin.<br />
                Eğer sınıflarınız varsa ama burada görünmüyorsa,<br />
                "Yeni Sınıf Oluştur" butonuna tıklayarak yeni bir sınıf oluşturabilirsiniz.
              </Typography>
              <Alert severity="info" sx={{ mb: 2 }}>
                <Typography variant="body2">
                  <strong>Debug Bilgileri:</strong><br />
                  User ID: {user?.uid}<br />
                  Email: {user?.email}<br />
                  Username: {userData?.username}
                </Typography>
              </Alert>
              <Button
                variant="outlined"
                onClick={loadClasses}
                sx={{ mr: 1 }}
              >
                Yenile
              </Button>
            </CardContent>
          </Card>
        ) : (
          /* Classes List */
          <List sx={{ px: 0 }}>
            {classes.map((classItem, index) => (
              <React.Fragment key={classItem.id}>
                <Card sx={{ mb: 2, cursor: 'pointer' }}>
                  <CardContent sx={{ p: 2 }}>
                    <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                      <Box sx={{ flexGrow: 1 }} onClick={() => handleClassClick(classItem)}>
                        <Box display="flex" alignItems="center" mb={1}>
                          <ClassIcon sx={{ mr: 1, color: 'primary.main' }} />
                          <Typography variant="h6" fontWeight="bold" color="primary">
                            {classItem.className}
                          </Typography>
                        </Box>
                        
                        <Typography variant="body2" color="text.secondary" mb={1}>
                          {classItem.description || 'Açıklama yok'}
                        </Typography>
                        
                        <Box display="flex" alignItems="center" gap={2} mb={1}>
                          <Chip
                            icon={<GroupIcon />}
                            label={`${classItem.studentCount} öğrenci`}
                            size="small"
                            color="secondary"
                          />
                          <Chip
                            icon={<CodeIcon />}
                            label={`Join: ${classItem.joinCode}`}
                            size="small"
                            variant="outlined"
                          />
                        </Box>
                        
                        {classItem.level && (
                          <Chip
                            label={`Seviye: ${classItem.level}`}
                            size="small"
                            color="info"
                          />
                        )}
                      </Box>
                      
                      {/* Delete Button */}
                      <MuiIconButton
                        color="error"
                        onClick={(e) => {
                          e.stopPropagation()
                          openDeleteDialog(classItem)
                        }}
                        sx={{ ml: 1 }}
                      >
                        <DeleteIcon />
                      </MuiIconButton>
                    </Box>
                  </CardContent>
                </Card>
                {index < classes.length - 1 && <Divider sx={{ my: 1 }} />}
              </React.Fragment>
            ))}
          </List>
        )}
      </Container>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={closeDeleteDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Sınıfı Sil
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            <strong>'{classToDelete?.className}'</strong> sınıfını silmek istediğinizden emin misiniz?
            <br /><br />
            Bu işlem geri alınamaz ve sınıftaki tüm öğrenciler sınıftan çıkarılacaktır.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDeleteDialog}>
            İptal
          </Button>
          <Button onClick={handleDeleteClass} color="error" variant="contained">
            Sil
          </Button>
        </DialogActions>
      </Dialog>
    </SpaceTheme>
  )
}

export default TeacherClasses
